import { provide } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { gameApiContext } from '../context';
import { GameAPI } from '../GameAPI';
import { tokens } from '../theme/tokens.css';
import { uiState as uiStateProxy, type UIState } from '../UIState';
import { bindValtio } from '../ValtioController';

import './components/big-message';
import './components/context-menu';
import './game/airmash-game';
import './lobby/airmash-lobby';
import './panels/help-panel';
import './panels/invite-panel';
import './panels/keybinds-panel';
import './panels/login-selector-panel';
import './panels/radio-config-panel';
import './panels/score-panel';

/**
 * Airmash UI - Root UI component
 *
 * Manages screen switching between lobby and game views.
 * Subscribes to the live UIState Valtio proxy via `bindValtio` and provides
 * GameAPI to descendants via Lit context.
 */
@customElement('airmash-ui')
export class AirmashUI extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        /* The base typography, declared once here from the tokens for every component below to inherit;
         main.css holds page policy only, and components override what differs. */
        font-family: var(--font-family);
        font-size: var(--font-size-13);
        color: var(--color-text-primary);
      }

      airmash-lobby,
      airmash-game {
        display: none;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }

      :host([screen='lobby']) airmash-lobby,
      :host([screen='game']) airmash-game {
        display: block;
      }

      /* F4 hide-all-UI mode — keeps the gameplay canvas visible (it lives
       outside this shadow DOM) but suppresses every Lit-rendered overlay. */
      :host([ui-hidden]) > * {
        display: none !important;
      }

      /* Panel overlay — sits above ALL game chrome. The z ladder across the
       app's shared stacking context: game-side components stay ≤201 (the top
       bar and the game selector lift to 200 while open, the mobile chat
       drawer is 201), panels 300, context-menu 400, big-message 500. At 100
       this sat UNDER the mobile chat drawer and toolbars — a panel opened via
       a typed /flag rendered behind the drawer. The emotes panel (chat-area
       lifts to 300 while it's open, anchored to the chat box) and the debug
       popup share the 300 tier but render outside this overlay. */
      .panel-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 300;
      }

      .panel-overlay > * {
        pointer-events: auto;
      }
    `,
  ];

  /** Current active screen (derived from state.screen, reflected to attribute for CSS) */
  @property({ reflect: true }) screen: UIState['screen'] = 'lobby';

  /** F4 toggle — when true, all rendered UI is hidden (gameplay canvas remains). */
  @property({ type: Boolean, reflect: true, attribute: 'ui-hidden' }) uiHidden = false;

  /** UIState — bound to the live Valtio proxy. Reads via `this.uiState.x` are
   *  always current; subscriptions are managed by the controller. */
  private uiState = bindValtio(this, uiStateProxy);

  /** Sync attribute-reflected props from uiState before each render so CSS
   *  selectors (`:host([screen='game'])`, `:host([ui-hidden])`) stay in step. */
  protected override willUpdate(): void {
    if (this.screen !== this.uiState.screen) {
      this.screen = this.uiState.screen;
    }
    if (this.uiHidden !== this.uiState.uiHidden) {
      this.uiHidden = this.uiState.uiHidden;
    }
    // The dev-only panels are fetched on first open rather than imported
    // statically. DEVELOPMENT is a runtime check (`?dev` works on the live site
    // too), so these modules can't be dead-code eliminated — a static import
    // would put them in the main chunk for every player. Same treatment eruda
    // already gets. Each element renders below immediately and upgrades when
    // its module resolves.
    if (this.uiState.debugStats && !this.debugPanelRequested) {
      this.debugPanelRequested = true;
      void import('./panels/debug-panel');
    }
    if (this.uiState.devTestPanel && !this.devTestPanelRequested) {
      this.devTestPanelRequested = true;
      void import('./components/dev-test-panel');
    }
    if (this.uiState.hitchLogOn && !this.hitchPanelRequested) {
      this.hitchPanelRequested = true;
      void import('./panels/dev-hitch-panel');
    }
  }

  /** Guard the one-shot dynamic imports above — not reactive state, just
   *  latches so reopening a panel doesn't re-request its module. */
  private debugPanelRequested = false;
  private devTestPanelRequested = false;
  private hitchPanelRequested = false;

  /** Provide GameAPI to all children. Protected rather than private: nothing
   *  here reads it, which noUnusedLocals would flag on a private field. */
  @provide({ context: gameApiContext })
  protected readonly gameApi = GameAPI;

  override render() {
    return html`
      <airmash-lobby></airmash-lobby>
      <airmash-game></airmash-game>

      <!-- Panel overlay - renders above all other UI -->
      <div class="panel-overlay">${this.renderActivePanel()}</div>

      <!-- Context menu - renders above panels -->
      ${this.uiState.contextMenu ? html`<context-menu></context-menu>` : nothing}

      <!-- Debug panel (development only) -->
      ${this.uiState.debugStats ? html`<debug-panel></debug-panel>` : nothing}

      <!-- Big message overlay - blocks all interaction -->
      ${this.uiState.bigMessage ? html`<big-message></big-message>` : nothing}

      <!-- Dev test panel (?DEV URL param, toggled via debug-panel button) -->
      ${this.uiState.devTestPanel ? html`<dev-test-panel></dev-test-panel>` : nothing}

      <!-- Dev hitch log, up while the log runs (debug-panel's HITCH LOG button) -->
      ${this.uiState.hitchLogOn ? html`<dev-hitch-panel></dev-hitch-panel>` : nothing}
    `;
  }

  /** Render the currently active panel. Every PanelType is named, so a new one
   *  is a compile error (noImplicitReturns) until it has a place. */
  private renderActivePanel() {
    switch (this.uiState.activePanel) {
      case 'login':
        return html`<login-selector-panel></login-selector-panel>`;
      case 'help':
        return html`<help-panel></help-panel>`;
      case 'score':
        return html`<score-panel></score-panel>`;
      case 'invite':
        return html`<invite-panel></invite-panel>`;
      case 'keybinds':
        return html`<keybinds-panel></keybinds-panel>`;
      case 'radioconfig':
        return html`<radio-config-panel></radio-config-panel>`;
      // Drawn elsewhere: the menu by menu-drawer, emotes by chat-area
      case 'mainmenu':
      case 'emotes':
      case null:
        return nothing;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'airmash-ui': AirmashUI;
  }
}
